import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Navigation } from 'lucide-react';
import type { StationRecommendation, Location } from '@/types';
import { formatPrice, formatDistanceAndTime, formatRelativeTime } from '@/utils/formatters';
import { getBrandLogo, getLogoUrl } from '@/utils/brandLogos';

interface StationMarkerProps {
  recommendation: StationRecommendation;
  isSelected?: boolean;
  onClick?: (recommendation: StationRecommendation) => void;
  tripType?: 'single' | 'return';
  selectedLocation?: Location | null;
  p25FuelPrice?: number;
  p75FuelPrice?: number;
}

function buildDirectionsUrl(destination: Location, origin?: Location | null): string {
  const dest = `${destination.latitude},${destination.longitude}`;
  if (origin) {
    const orig = `${origin.latitude},${origin.longitude}`;
    return `https://www.google.com/maps/dir/?api=1&origin=${orig}&destination=${dest}&travelmode=driving`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
}

/**
 * Create custom marker icon with brand logo and price display
 */
function createBrandMarkerIcon(
  brand: string, 
  priceLevel: 'cheap' | 'medium' | 'expensive', 
  isSelected = false,
  priceText?: string
): L.DivIcon {
  const logoInfo = getBrandLogo(brand);
  const logoUrl = getLogoUrl(logoInfo);

  // Create logo marker with price indicator
  const priceClass = `price-${priceLevel}`;
  const selectedClass = isSelected ? 'selected-marker' : '';

  // Escape special characters in strings for HTML attributes
  const escapedName = logoInfo.name.replace(/'/g, '&apos;').replace(/"/g, '&quot;');

  // SVG fallback for when the primary logoUrl fails to load
  const svgFallbackUrl = logoInfo.svg
    ? `data:image/svg+xml,${encodeURIComponent(logoInfo.svg.trim())}`
    : `data:image/svg+xml,${encodeURIComponent('<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="50" r="45" fill="#6B7280"/><text x="50" y="58" text-anchor="middle" fill="white" font-family="Arial,sans-serif" font-size="24">⛽</text></svg>')}`;

  // Create price display if price is available
  const priceDisplay = priceText ? `
    <div class="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-white border border-gray-300 rounded px-1.5 py-0.5 text-xs font-medium text-gray-900 shadow-sm whitespace-nowrap">
      ${priceText}
    </div>
  ` : '';

  return L.divIcon({
    html: `
      <div class="relative w-10 h-10">
        <img
          src="${logoUrl}"
          alt="${escapedName}"
          onerror="this.onerror=null;this.src='${svgFallbackUrl}'"
          class="w-10 h-10 object-contain shadow-lg ${priceClass} ${selectedClass}"
          style="max-width: 40px; max-height: 40px; width: 40px; height: 40px;"
        />
        ${isSelected ? '<div class="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full border-2 border-white shadow-sm"></div>' : ''}
        ${priceDisplay}
      </div>
    `,
    className: 'fuel-brand-marker',
    iconSize: [40, 50], // Increased height to accommodate price display
    iconAnchor: [20, 25], // Adjusted anchor point
    popupAnchor: [0, -25], // Adjusted popup anchor
  });
}


export const StationMarker: React.FC<StationMarkerProps> = React.memo(({
  recommendation,
  isSelected = false,
  onClick,
  tripType = 'single',
  selectedLocation,
  p25FuelPrice = 0,
  p75FuelPrice = 0,
}) => {
  const { station, bestPrice, distance, travelCost, totalCost, savings } = recommendation;

  // Create marker icon with brand logo and price level
  const markerIcon = useMemo(() => {
    // Quartile-based classification (P25/P75 locked at search time)
    const priceLevel: 'cheap' | 'medium' | 'expensive' =
      p25FuelPrice === 0 || !bestPrice?.price_pence ? 'medium' :
      bestPrice.price_pence <= p25FuelPrice         ? 'cheap' :
      bestPrice.price_pence >= p75FuelPrice         ? 'expensive' :
                                                      'medium';
    
    // Format price text for display with three decimal places
    const priceText = bestPrice?.price_pence ? formatPrice(bestPrice.price_pence) : undefined;
    
    return createBrandMarkerIcon(station.brand, priceLevel, isSelected, priceText);
  }, [station.brand, bestPrice?.price_pence, isSelected]);

  // Memoize the click handler to prevent unnecessary re-renders
  const handleClick = useCallback(() => {
    onClick?.(recommendation);
  }, [onClick, recommendation]);

  const markerRef = useRef<L.Marker | null>(null);

  // Reopen popup when this marker becomes selected.
  // Covers: popup closed by Leaflet's setIcon re-bind, autoPan-triggered mode switches,
  // or first-mount when selected (e.g., from list panel selection).
  useEffect(() => {
    if (isSelected && markerRef.current) {
      const timer = setTimeout(() => {
        markerRef.current?.openPopup();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isSelected]);

  return (
    <Marker
      ref={markerRef}
      position={[station.location.latitude, station.location.longitude]}
      icon={markerIcon}
      eventHandlers={{
        click: handleClick,
      }}
    >
      <Popup>
        <div className="min-w-[200px]">
          {/* Coloured left-border stripe by price level */}
          <div className={`border-l-4 p-2 ${
            bestPrice?.price_pence
              ? bestPrice.price_pence < 140 ? 'border-green-400'
              : bestPrice.price_pence > 160 ? 'border-red-400'
              : 'border-amber-400'
              : 'border-gray-300'
          }`}>
            <div className="font-semibold text-gray-900 mb-1">
              {station.brand}
            </div>

            <div className="text-sm text-gray-600 mb-2">
              {station.address}
            </div>

            {bestPrice && (
              <div className="space-y-1 mb-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{bestPrice.fuel_name}:</span>
                  <span className="font-medium text-gray-900">
                    {formatPrice(bestPrice.price_pence)}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Distance:</span>
                  <span className="text-sm text-gray-700">
                    {formatDistanceAndTime(
                      tripType === 'return' ? distance * 2 : distance,
                      tripType === 'return' && recommendation.travelTime ? recommendation.travelTime * 2 : recommendation.travelTime
                    )}
                  </span>
                </div>

                {travelCost > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Travel cost:</span>
                    <span className="text-sm text-gray-700">
                      {formatPrice(travelCost)}
                    </span>
                  </div>
                )}

                <div className="flex justify-between border-t pt-1">
                  <span className="text-sm font-medium text-gray-900">Total cost:</span>
                  <span className="text-base font-semibold text-gray-900">
                    {formatPrice(travelCost > 0 ? totalCost : bestPrice?.price_pence)}
                  </span>
                </div>

                {/* Savings chip */}
                {savings !== undefined && savings > 0 && (
                  <div className="pt-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      Save {formatPrice(savings)}
                    </span>
                  </div>
                )}
                {savings !== undefined && savings < -10 && (
                  <div className="pt-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      +{formatPrice(Math.abs(savings))} vs avg
                    </span>
                  </div>
                )}
              </div>
            )}

            {(bestPrice?.recorded_at || bestPrice?.imported_at) && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                {bestPrice?.imported_at && (
                  <div className="text-xs text-gray-600">
                    Data fetched {formatRelativeTime(bestPrice.imported_at)}
                  </div>
                )}
                {bestPrice?.recorded_at && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    Price last updated {formatRelativeTime(bestPrice.recorded_at)}
                  </div>
                )}
              </div>
            )}

            <a
              href={buildDirectionsUrl(station.location, selectedLocation)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-1.5 w-full px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Navigation className="w-3.5 h-3.5" />
              Get Directions
            </a>
          </div>
        </div>
      </Popup>
    </Marker>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to optimize re-renders
  return (
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.tripType === nextProps.tripType &&
    prevProps.recommendation.station.id === nextProps.recommendation.station.id &&
    prevProps.recommendation.bestPrice?.price_pence === nextProps.recommendation.bestPrice?.price_pence &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.selectedLocation?.latitude === nextProps.selectedLocation?.latitude &&
    prevProps.selectedLocation?.longitude === nextProps.selectedLocation?.longitude &&
    prevProps.p25FuelPrice === nextProps.p25FuelPrice &&
    prevProps.p75FuelPrice === nextProps.p75FuelPrice
  );
});

StationMarker.displayName = 'StationMarker';

export default StationMarker;